import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, LogIn } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { authApi } from '../../lib/api/auth'
import './auth.css'

export default function SignIn() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthStore()
  const justRegistered = (location.state as { registered?: boolean } | null)?.registered === true

  const [email, setEmail]               = useState('')
  const [password, setPassword]         = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError]               = useState('')
  const [loading, setLoading]           = useState(false)

  // Brute-force protection
  const [failCount, setFailCount]   = useState(0)
  const [lockUntil, setLockUntil]   = useState<number | null>(null)
  const [countdown, setCountdown]   = useState(0)
  const timerRef                    = useRef<ReturnType<typeof setInterval> | null>(null)

  // Run countdown ticker when locked
  useEffect(() => {
    if (lockUntil === null) return

    const tick = () => {
      const remaining = Math.ceil((lockUntil - Date.now()) / 1000)
      if (remaining <= 0) {
        setCountdown(0)
        setLockUntil(null)
        if (timerRef.current) clearInterval(timerRef.current)
      } else {
        setCountdown(remaining)
      }
    }

    tick()
    timerRef.current = setInterval(tick, 500)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [lockUntil])

  const getLockDuration = (count: number): number => {
    if (count >= 15) return 120
    if (count >= 10) return 60
    if (count >= 5)  return 30
    return 0
  }

  const isLocked = lockUntil !== null && Date.now() < lockUntil

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLocked) return

    setError('')
    setLoading(true)

    try {
      const data = await authApi.login(email, password)
      const role = data.user?.role
      if (!role) {
        setError('Login failed: invalid response. Please try again.')
        return
      }
      login(data.token, data.user)
      navigate(role === 'client' ? '/client/profile' : `/${role}`, { replace: true })
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Invalid email or password'

      const newCount = failCount + 1
      setFailCount(newCount)

      const lockSecs = getLockDuration(newCount)
      if (lockSecs > 0) {
        setLockUntil(Date.now() + lockSecs * 1000)
        setError(`Too many attempts. Try again in ${lockSecs}s`)
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">

        {/* ── Left panel ── */}
        <div className="auth-left">
          <div className="auth-brand">
            <Link to="/"><img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="auth-logo" /></Link>
            <h1>Interview Dress Rehearsal</h1>
            <p>Practice makes perfect. Prepare for your dream job with professional mock interviews.</p>
          </div>

          <div className="auth-features">
            {[
              { label: 'Professional interview practice' },
              { label: 'Expert feedback & evaluation' },
              { label: 'AI-powered question preparation' },
            ].map(({ label }) => (
              <div key={label} className="feature-item">
                <div className="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                </div>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="auth-right">

          {/* Mobile-only brand header — hidden on desktop where left panel shows */}
          <div className="auth-mobile-brand">
            <Link to="/">
              <img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="auth-mobile-logo" />
            </Link>
            <div className="auth-mobile-brand-text">
              <span className="auth-mobile-name">Interview Dress Rehearsal</span>
              <span className="auth-mobile-tagline">Practice smart. Interview with confidence.</span>
            </div>
          </div>

          <div className="auth-card">
            <div className="auth-header">
              <h2>Welcome back</h2>
              <p>Sign in to your account to continue</p>
            </div>

            {justRegistered && (
              <div className="auth-success">
                Account created! Please sign in to continue.
              </div>
            )}

            {isLocked && countdown > 0 && (
              <div className="auth-error">
                Too many attempts. Try again in {countdown}s
              </div>
            )}
            {!isLocked && error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label" htmlFor="signin-email">Email</label>
                <input
                  id="signin-email"
                  type="email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="signin-password">Password</label>
                <div className="password-input">
                  <input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-options">
                <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
              </div>

              <button type="submit" className="btn btn-primary auth-submit" disabled={loading || isLocked}>
                {loading ? 'Signing in…' : <><LogIn size={18} /> Sign In</>}
              </button>
            </form>

            <div className="auth-footer">
              <p>Don't have an account? <Link to="/signup">Sign up</Link></p>
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}
